import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Infinity as InfinityIcon, Plus, Eye, Search, SlidersHorizontal, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSite } from "../context/SiteContext";
import { img } from "../lib/api";

const badgeStyles = {
  popular: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  new: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  best: "bg-gold/15 text-gold border-gold/40",
};

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "low", label: "Price: Low to High" },
  { id: "high", label: "Price: High to Low" },
  { id: "name", label: "Name A–Z" },
];

const fieldCls = "bg-navy/80 border border-white/10 focus:border-gold text-white rounded-xl px-4 py-2.5 outline-none transition-colors text-sm";

const minPrice = (p) => (p.options?.length ? Math.min(...p.options.map((o) => o.price)) : p.priceFrom || 0);

const ProductCard = ({ p, idx, onView, onAdd }) => (
  <div className="fade-up card-hover glass-navy-card rounded-2xl overflow-hidden flex flex-col" style={{ animationDelay: `${idx * 60}ms` }} data-testid={`product-card-${p.id}`}>
    <div className="relative img-zoom h-56 cursor-pointer" onClick={onView}>
      <img src={img(p.image || p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-800/90 to-transparent" />
      {p.badge && <span className={`absolute top-4 left-4 text-[11px] font-semibold px-3 py-1 rounded-full border ${badgeStyles[p.badgeType] || badgeStyles.new}`}>{p.badge}</span>}
      <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[11px] text-slate-200 bg-navy/70 backdrop-blur px-2.5 py-1 rounded-full"><InfinityIcon size={13} className="text-gold" /> {p.stock}</span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <p className="text-xs text-gold/80 mb-1">{p.category}</p>
      <h3 className="text-xl font-bold text-white">{p.name}</h3>
      <p className="mt-2 text-slate-400 text-sm line-clamp-2 flex-1">{p.description}</p>
      <div className="mt-4">
        <p className="text-gold font-display font-bold text-xl" data-testid={`product-price-${p.id}`}>{p.priceLabel}</p>
        <p className="text-xs text-emerald-400">{p.delivery}</p>
      </div>
      <div className="mt-5 flex gap-3">
        <button onClick={onView} data-testid={`product-details-btn-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/30 hover:border-gold text-white text-sm font-medium py-2.5 rounded-xl transition-colors duration-200"><Eye size={16} /> Details</button>
        <button onClick={onAdd} data-testid={`product-add-btn-${p.id}`} className="flex-1 btn-liquid text-navy text-sm font-semibold py-2.5 rounded-xl"><span className="relative z-10 inline-flex items-center gap-2"><Plus size={16} /> Add</span></button>
      </div>
    </div>
  </div>
);

const Products = () => {
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [maxPrice, setMaxPrice] = useState("");
  const { addItem } = useCart();
  const { products } = useSite();
  const navigate = useNavigate();

  const categories = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))], [products]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = products.filter((p) =>
      (active === "All" || p.category === active) &&
      (!term || `${p.name} ${p.description} ${p.category}`.toLowerCase().includes(term)) &&
      (!maxPrice || minPrice(p) <= Number(maxPrice)));
    if (sort === "low") list = [...list].sort((a, b) => minPrice(a) - minPrice(b));
    if (sort === "high") list = [...list].sort((a, b) => minPrice(b) - minPrice(a));
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [active, q, sort, maxPrice, products]);

  const reset = () => { setActive("All"); setQ(""); setSort("featured"); setMaxPrice(""); };

  return (
    <section id="products" className="bg-navy py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="reveal max-w-2xl mb-12">
          <p className="text-gold text-sm font-semibold tracking-widest uppercase mb-3">Our Catalog</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">Shop the <span className="liquid-gold-text">Collection</span></h2>
          <p className="mt-4 text-slate-400">Premium prop bills and bundles, delivered instantly with discreet shipping.</p>
        </div>

        <div className="glass-navy-card rounded-2xl p-4 sm:p-5 mb-10 space-y-4" data-testid="product-filter-bar">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="product-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className={`${fieldCls} w-full pl-10`} />
            </div>
            <select data-testid="product-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={fieldCls}>
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <div className="relative">
              <SlidersHorizontal size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="product-max-price-input" type="number" min="0" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max price $" className={`${fieldCls} pl-10 w-full md:w-40`} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)} data-testid={`category-pill-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-xl text-sm transition-colors duration-200 border ${active === c ? "bg-gold text-navy border-gold font-semibold" : "text-slate-300 border-white/10 hover:border-gold/40 hover:text-gold"}`}>{c}</button>
            ))}
          </div>
        </div>

        <p className="text-slate-400 text-sm mb-5" data-testid="product-results-count">{filtered.length} product{filtered.length === 1 ? "" : "s"}</p>
        {filtered.length === 0 ? (
          <div className="text-center py-16 glass-navy-card rounded-2xl" data-testid="product-empty-state">
            <p className="text-slate-300">No products match your filters.</p>
            <button onClick={reset} className="mt-4 inline-flex items-center gap-2 text-gold hover:underline text-sm"><X size={14} /> Clear filters</button>
          </div>
        ) : (
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3" key={`${active}-${sort}`}>
            {filtered.map((p, idx) => (
              <ProductCard key={p.id} p={p} idx={idx} onView={() => navigate(`/product/${p.id}`)} onAdd={() => addItem(p, p.options[0])} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Products;
